// Admin component to run image migration from base64 to Firebase Storage
import React, { useState, useEffect } from 'react';
import { Play, RefreshCw, Database, Cloud, AlertTriangle, CheckCircle, Shield, User, ExternalLink } from 'lucide-react';
import { runImageMigration, analyzeExistingImages } from '../../utils/migrationScript';
import { getRecentlyMigratedBrands, getBrandMigrationStatus } from '../../utils/verifyMigration';
import { useAuth } from '../../context/AuthContext';

function ImageMigrationRunner() {
  const { user } = useAuth();
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isMigrating, setIsMigrating] = useState(false);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [migrationResults, setMigrationResults] = useState(null);
  const [showCorsHelp, setShowCorsHelp] = useState(false);
  const [recentlyMigrated, setRecentlyMigrated] = useState([]);
  const [isLoadingRecent, setIsLoadingRecent] = useState(false);
  const [migrationOptions, setMigrationOptions] = useState({
    dryRun: true,
    brandLimit: 1, // Start with 1 for testing
    skipPromotions: false
  });

  const handleAnalyze = async () => {
    setIsAnalyzing(true);
    try {
      const results = await analyzeExistingImages();
      setAnalysisResults(results);
    } catch (error) {
      console.error('Analysis failed:', error);
      alert(`Analysis failed: ${error.message}`);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const loadRecentlyMigrated = async () => {
    setIsLoadingRecent(true);
    try {
      const recent = await getRecentlyMigratedBrands();
      setRecentlyMigrated(recent);
    } catch (error) {
      console.error('Failed to load recently migrated brands:', error);
    } finally {
      setIsLoadingRecent(false);
    }
  };

  // Load recently migrated brands on component mount
  useEffect(() => {
    if (user) {
      loadRecentlyMigrated();
    }
  }, [user]);

  const handleMigration = async () => {
    if (!migrationOptions.dryRun) {
      const confirmed = window.confirm(
        `⚠️ LIVE MIGRATION WARNING ⚠️\n\n` +
        `This will modify your database and upload files to Firebase Storage.\n` +
        `${migrationOptions.brandLimit ? `Limited to ${migrationOptions.brandLimit} brands.` : 'All brands will be processed.'}\n\n` +
        `Are you sure you want to proceed?`
      );
      if (!confirmed) return;
    }

    setIsMigrating(true);
    setMigrationResults(null);

    try {
      console.log('🚀 Starting migration with options:', migrationOptions);
      const results = await runImageMigration(migrationOptions);
      console.log('✅ Migration completed:', results);
      setMigrationResults(results);

      if (results && !results.isDryRun && results.totalImages > 0) {
        alert(`🎉 Migration completed! ${results.totalImages} images successfully migrated to Firebase Storage.`);
        // Refresh recently migrated brands list
        setTimeout(() => loadRecentlyMigrated(), 1000);
      }
    } catch (error) {
      console.error('❌ Migration failed:', error);

      // Check for CORS error specifically
      if (error.message.includes('CORS') || error.message.includes('ERR_FAILED')) {
        setShowCorsHelp(true);
      }

      setMigrationResults({
        isDryRun: migrationOptions.dryRun,
        processedBrands: 0,
        brandImages: 0,
        promotionImages: 0,
        totalImages: 0,
        errors: 1,
        errorMessage: error.message,
        isCorsError: error.message.includes('CORS') || error.message.includes('ERR_FAILED')
      });

      if (error.message.includes('CORS') || error.message.includes('ERR_FAILED')) {
        alert(`🚨 CORS Error Detected!\n\nFirebase Storage is blocking uploads from localhost:3000.\n\nPlease follow the CORS configuration instructions that will appear below.`);
      } else {
        alert(`Migration failed: ${error.message}\n\nCheck the browser console for more details.`);
      }
    } finally {
      setIsMigrating(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 text-white">
          <h2 className="text-2xl font-bold flex items-center">
            <Cloud className="mr-3" />
            Image Migration Tool
          </h2>
          <p className="text-blue-100 mt-2">
            Convert existing base64 images in your database to Firebase Storage files
          </p>
        </div>

        <div className="p-6 space-y-6">
          {/* Authentication Status */}
          <div className={`border rounded-lg p-4 ${user ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
            <div className="flex items-center">
              {user ? (
                <>
                  <Shield className="h-5 w-5 text-green-600 mr-2" />
                  <span className="font-medium text-green-800">
                    ✅ Authenticated as: {user.email || user.uid}
                  </span>
                </>
              ) : (
                <>
                  <User className="h-5 w-5 text-red-600 mr-2" />
                  <span className="font-medium text-red-800">
                    ❌ Not authenticated - Please sign in first
                  </span>
                </>
              )}
            </div>
            {!user && (
              <p className="text-red-700 text-sm mt-2">
                You must be signed in to run the migration. Please log in and return to this page.
              </p>
            )}
          </div>

          {/* CORS Help Section */}
          {showCorsHelp && (
            <div className="border border-orange-200 bg-orange-50 rounded-lg p-4">
              <h3 className="font-medium text-orange-800 mb-3 flex items-center">
                <AlertTriangle className="h-5 w-5 mr-2" />
                🚨 CORS Error Detected - Firebase Storage Configuration Needed
              </h3>

              <div className="space-y-3 text-sm text-orange-700">
                <p>Firebase Storage is blocking uploads from localhost:3000. Here's how to fix it:</p>

                <div className="bg-white p-3 rounded border border-orange-200">
                  <h4 className="font-medium mb-2">Quick Fix (Recommended):</h4>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Install Google Cloud SDK: <code className="bg-gray-100 px-1 rounded">brew install google-cloud-sdk</code></li>
                    <li>Authenticate: <code className="bg-gray-100 px-1 rounded">gcloud auth login</code></li>
                    <li>Set project: <code className="bg-gray-100 px-1 rounded">gcloud config set project indobrand-1e09e</code></li>
                    <li>Apply CORS: <code className="bg-gray-100 px-1 rounded">gsutil cors set cors.json gs://indobrand-1e09e.appspot.com</code></li>
                  </ol>
                </div>

                <div className="bg-white p-3 rounded border border-orange-200">
                  <h4 className="font-medium mb-2">Alternative - Update Storage Rules:</h4>
                  <p>Go to Firebase Console → Storage → Rules and ensure authenticated users can upload.</p>
                  <a
                    href="https://console.firebase.google.com/project/indobrand-1e09e/storage/indobrand-1e09e.appspot.com/rules"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-blue-600 hover:text-blue-800 mt-2"
                  >
                    Open Firebase Storage Rules <ExternalLink className="h-3 w-3 ml-1" />
                  </a>
                </div>

                <div className="bg-yellow-100 border border-yellow-300 rounded p-2">
                  <p className="text-yellow-800 text-xs">
                    📝 A <code>cors.json</code> file has been created in your project root with the correct configuration.
                  </p>
                </div>

                <button
                  onClick={() => setShowCorsHelp(false)}
                  className="bg-orange-600 text-white px-3 py-1 rounded text-sm hover:bg-orange-700"
                >
                  Hide Instructions
                </button>
              </div>
            </div>
          )}

          {/* Analysis Section */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Database className="mr-2 text-blue-600" />
              Step 1: Analyze Current Data
            </h3>

            <p className="text-gray-600 mb-4">
              First, let's see how many base64 images you currently have stored in your database.
            </p>

            <button
              onClick={handleAnalyze}
              disabled={isAnalyzing || !user}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isAnalyzing ? (
                <RefreshCw className="animate-spin mr-2 h-4 w-4" />
              ) : (
                <Database className="mr-2 h-4 w-4" />
              )}
              {isAnalyzing ? 'Analyzing...' : 'Analyze Database'}
            </button>

            {analysisResults && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <h4 className="font-medium mb-3">📊 Analysis Results:</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-700">Total Brands</div>
                    <div className="text-2xl font-bold text-blue-600">{analysisResults.brands}</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-orange-700">To Migrate</div>
                    <div className="text-2xl font-bold text-orange-600">{analysisResults.toMigrate.total}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {analysisResults.toMigrate.brandLogos} logos, {analysisResults.toMigrate.brandCovers} covers, {analysisResults.toMigrate.promotions} promotions
                    </div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-green-700">Already Migrated</div>
                    <div className="text-2xl font-bold text-green-600">{analysisResults.migrated.total}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {analysisResults.migrated.brandLogos} logos, {analysisResults.migrated.brandCovers} covers, {analysisResults.migrated.promotions} promotions
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Migration Options */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Cloud className="mr-2 text-purple-600" />
              Step 2: Configure Migration
            </h3>

            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={migrationOptions.dryRun}
                    onChange={(e) => setMigrationOptions(prev => ({ ...prev, dryRun: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Dry Run (preview only, no changes)
                  </span>
                </label>
              </div>

              <div className="flex items-center space-x-4">
                <label className="text-sm font-medium text-gray-700">
                  Brand Limit:
                </label>
                <select
                  value={migrationOptions.brandLimit || ''}
                  onChange={(e) => setMigrationOptions(prev => ({
                    ...prev,
                    brandLimit: e.target.value ? parseInt(e.target.value) : null
                  }))}
                  className="border border-gray-300 rounded px-3 py-1 text-sm"
                >
                  <option value="">All Brands</option>
                  <option value="1">1 Brand (Test)</option>
                  <option value="5">5 Brands</option>
                  <option value="10">10 Brands</option>
                  <option value="25">25 Brands</option>
                </select>
              </div>

              <div className="flex items-center space-x-4">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={migrationOptions.skipPromotions}
                    onChange={(e) => setMigrationOptions(prev => ({ ...prev, skipPromotions: e.target.checked }))}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-2 text-sm font-medium text-gray-700">
                    Skip Promotion Images (only migrate brand logos/covers)
                  </span>
                </label>
              </div>
            </div>

            {!migrationOptions.dryRun && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5 mr-2" />
                  <div className="text-sm text-yellow-800">
                    <strong>Live Migration Mode:</strong> This will upload files to Firebase Storage and modify your database.
                    Make sure you have a backup before proceeding.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Migration Execution */}
          <div className="border border-gray-200 rounded-lg p-4">
            <h3 className="text-lg font-semibold mb-4 flex items-center">
              <Play className="mr-2 text-green-600" />
              Step 3: Run Migration
            </h3>

            <button
              onClick={handleMigration}
              disabled={isMigrating || !user}
              className={`inline-flex items-center px-6 py-3 rounded-lg font-medium ${
                migrationOptions.dryRun
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
              } disabled:opacity-50`}
            >
              {isMigrating ? (
                <RefreshCw className="animate-spin mr-2 h-4 w-4" />
              ) : (
                <Play className="mr-2 h-4 w-4" />
              )}
              {isMigrating
                ? 'Running Migration...'
                : migrationOptions.dryRun
                  ? 'Preview Migration (Dry Run)'
                  : 'Run Live Migration'
              }
            </button>

            {migrationResults && (
              <div className={`mt-4 p-4 rounded-lg ${migrationResults.isDryRun ? 'bg-blue-50' : 'bg-green-50'}`}>
                <h4 className="font-medium mb-3 flex items-center">
                  {migrationResults.isDryRun ? (
                    <>
                      <Database className="mr-2 text-blue-600" />
                      Dry Run Results (Preview):
                    </>
                  ) : (
                    <>
                      <CheckCircle className="mr-2 text-green-600" />
                      Migration Results:
                    </>
                  )}
                </h4>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-4">
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-700">Brands Processed</div>
                    <div className="text-xl font-bold text-blue-600">{migrationResults.processedBrands}</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-700">Brand Images</div>
                    <div className="text-xl font-bold text-green-600">{migrationResults.brandImages}</div>
                    {migrationResults.breakdown && (
                      <div className="text-xs text-gray-500 mt-1">
                        {migrationResults.breakdown.brandLogos} logos, {migrationResults.breakdown.brandCovers} covers
                      </div>
                    )}
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-700">Promotion Images</div>
                    <div className="text-xl font-bold text-purple-600">{migrationResults.promotionImages}</div>
                  </div>
                  <div className="bg-white p-3 rounded border">
                    <div className="font-medium text-gray-700">
                      {migrationResults.isDryRun ? 'Would Migrate' : 'Total Migrated'}
                    </div>
                    <div className="text-xl font-bold text-green-600">{migrationResults.totalImages}</div>
                  </div>
                </div>

                {migrationResults.isDryRun && migrationResults.detailedBrands && migrationResults.detailedBrands.length > 0 && (
                  <div className="bg-white p-3 rounded border mb-3">
                    <h5 className="font-medium text-gray-700 mb-2">
                      Brands with Base64 Images ({migrationResults.detailedBrands.length}):
                    </h5>
                    <div className="space-y-1 max-h-40 overflow-y-auto">
                      {migrationResults.detailedBrands.map((brand, index) => (
                        <div key={brand.id} className="text-sm text-gray-600 flex justify-between">
                          <span className="truncate">{brand.name}</span>
                          <span className="text-xs ml-2 whitespace-nowrap">
                            {brand.hasBase64Logo && '📷 Logo '}
                            {brand.hasBase64Cover && '🖼️ Cover '}
                            {brand.promotionCount > 0 && `🎯 ${brand.promotionCount}promo`}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {migrationResults.isDryRun && migrationResults.totalImages === 0 && (
                  <div className="bg-green-100 border border-green-200 rounded p-3 text-green-700 text-sm">
                    🎉 No base64 images found! All your images are already migrated to Firebase Storage.
                  </div>
                )}

                {migrationResults.isDryRun && migrationResults.totalImages > 0 && (
                  <div className="bg-yellow-100 border border-yellow-200 rounded p-3 text-yellow-800 text-sm">
                    💡 Ready to migrate {migrationResults.totalImages} images. Uncheck "Dry Run" and click "Run Live Migration" to proceed.
                  </div>
                )}

                {migrationResults.errors > 0 && (
                  <div className="mt-3 p-2 bg-red-100 border border-red-200 rounded text-red-700 text-sm">
                    ⚠️ {migrationResults.errors} errors occurred during migration.
                    {migrationResults.errorMessage && (
                      <div className="mt-1 font-mono text-xs">
                        Error: {migrationResults.errorMessage}
                      </div>
                    )}
                    Check console for details.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Recently Migrated Brands */}
          <div className="border border-gray-200 rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold flex items-center">
                <CheckCircle className="mr-2 text-green-600" />
                Recently Migrated Brands
              </h3>
              <button
                onClick={loadRecentlyMigrated}
                disabled={isLoadingRecent || !user}
                className="inline-flex items-center px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200 disabled:opacity-50"
              >
                {isLoadingRecent ? (
                  <RefreshCw className="animate-spin mr-1 h-3 w-3" />
                ) : (
                  <RefreshCw className="mr-1 h-3 w-3" />
                )}
                Refresh
              </button>
            </div>

            {recentlyMigrated.length > 0 ? (
              <div className="space-y-3">
                {recentlyMigrated.slice(0, 5).map((brand, index) => (
                  <div key={brand.brandId} className="bg-green-50 border border-green-200 rounded p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-green-800">{brand.name}</h4>
                        <p className="text-green-600 text-sm">
                          Migrated: {brand.migratedAt?.toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right text-sm">
                        <div className="flex space-x-2">
                          {brand.logo.isFirebaseStorage && (
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Logo ✓</span>
                          )}
                          {brand.cover.isFirebaseStorage && (
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">Cover ✓</span>
                          )}
                          {brand.promotions.migrated > 0 && (
                            <span className="bg-green-100 text-green-700 px-2 py-1 rounded text-xs">
                              {brand.promotions.migrated} Promos ✓
                            </span>
                          )}
                        </div>
                        {brand.fullyMigrated && (
                          <div className="text-green-600 text-xs mt-1">✅ Fully Migrated</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {recentlyMigrated.length > 5 && (
                  <p className="text-gray-500 text-sm text-center">
                    ... and {recentlyMigrated.length - 5} more migrated brands
                  </p>
                )}
              </div>
            ) : (
              <div className="text-gray-500 text-center py-4">
                {isLoadingRecent ? (
                  <div className="flex items-center justify-center">
                    <RefreshCw className="animate-spin mr-2 h-4 w-4" />
                    Loading recently migrated brands...
                  </div>
                ) : (
                  <div>
                    <CheckCircle className="mx-auto h-8 w-8 text-gray-400 mb-2" />
                    <p>No recently migrated brands found.</p>
                    <p className="text-sm">Brands will appear here after successful migration.</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">💡 Instructions:</h4>
            <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
              <li><strong>Start with Analysis</strong> - See how many images need migration</li>
              <li><strong>Test with Dry Run</strong> - Use dry run mode to preview changes (recommended)</li>
              <li><strong>Start Small</strong> - Test with 1-5 brands first to verify everything works</li>
              <li><strong>Run Full Migration</strong> - Once tested, remove dry run and brand limit</li>
              <li><strong>Monitor Console</strong> - Check browser console for detailed progress logs</li>
            </ol>

            <div className="mt-4 p-3 bg-white border border-blue-200 rounded">
              <h5 className="font-medium text-blue-900 mb-2">🔍 How to Verify Migration:</h5>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• <strong>Recently Migrated section above</strong> - Shows brands with migration timestamp</li>
                <li>• <strong>Firebase Storage Console</strong> - Check for <code>brands/</code> folders with uploaded images</li>
                <li>• <strong>Brand pages</strong> - Images should load from <code>firebasestorage.googleapis.com</code> URLs</li>
                <li>• <strong>Browser Network tab</strong> - No more large base64 data in Firestore requests</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageMigrationRunner;